<script setup lang="ts">
/**
 * SiteHeader —— 全局页面 chrome 顶部（F17，ADR-0017）。
 *
 * 视觉结构对齐甲方参考图与 `project/index.html` / `detail.html` `.header`：
 * 红底 100px + 学校白色 logo（`/template/school_logo_white.png`，点击回首页）
 * + 成果标题 + 右侧 snail 白色 logo（`/template/snail_logo_white.png`）+
 * `::after` men.jpg 半透明遮罩。挂在 `App.vue`
 * 顶部，首页 + 详情页共享一套 chrome。
 *
 * **数据驱动**：标题主源 `SiteConfig.title`，副标题 `SiteConfig.subtitle`；
 * `idle` / `failed` 态 aggregate 为 null → 兜底 `copy.chrome.header.titleFallback`，
 * 让 chrome 在首屏数据未到时仍可见（chrome 是静态结构，不随内容状态闪隐）。
 *
 * **不读 store**：siteConfig 由 `App.vue` 透传 props，保持组件可独立 mount
 * 测试（与 AnchorNav / DetailSidebar 一致的纯渲染口径）。
 */
import { RouterLink } from 'vue-router';

import { copy } from '@/i18n/zh-CN';
import type { SiteConfigView } from '@/api/types';

const props = defineProps<{
  readonly siteConfig: SiteConfigView | null;
}>();

/** 标题：siteConfig.title 主源，idle/failed 兜底 copy.chrome.header.titleFallback。 */
function titleOf(siteConfig: SiteConfigView | null): string {
  if (siteConfig === null) {
    return copy.chrome.header.titleFallback;
  }
  return siteConfig.title !== '' ? siteConfig.title : copy.chrome.header.titleFallback;
}

/** 副标题：siteConfig.subtitle 非空才显（模板无副标题则整段不渲染）。 */
function subtitleOf(siteConfig: SiteConfigView | null): string {
  if (siteConfig === null) {
    return '';
  }
  return siteConfig.subtitle ?? '';
}
</script>

<template>
  <header :class="$style.header" data-testid="site-header">
    <div :class="$style.mask" data-shell="header-mask" aria-hidden="true" />
    <div :class="$style.inner">
      <RouterLink :class="$style.logoLink" to="/" data-testid="site-header-logo">
        <img :class="$style.logo" src="/template/school_logo_white.png" :alt="copy.chrome.header.logoAlt" />
      </RouterLink>
      <div :class="$style.titleGroup">
        <span :class="$style.title" data-testid="site-header-title">{{ titleOf(props.siteConfig) }}</span>
        <span v-if="subtitleOf(props.siteConfig) !== ''" :class="$style.subtitle" data-testid="site-header-subtitle">
          {{ subtitleOf(props.siteConfig) }}
        </span>
      </div>
      <div :class="$style.rightLogo" data-testid="site-header-right-logo" aria-hidden="true">
        <img :class="$style.rightLogoImg" src="/template/snail_logo_white.png" alt="" />
      </div>
    </div>
  </header>
</template>

<style module src="./SiteHeader.module.css"></style>
